import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageSquare,
  Mail,
  DollarSign,
  Check,
  ArrowLeft,
  Shield,
  Users,
  Settings,
  X
} from 'lucide-react';
import Button from '../utils/Button';

interface MessagingDisclosureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEnableMessaging: () => void;
  partnerId?: string;
}

export default function MessagingDisclosureModal({
  isOpen,
  onClose,
  onEnableMessaging,
  partnerId
}: MessagingDisclosureModalProps) {
  const [isAcknowledging, setIsAcknowledging] = useState(false);

  const handleEnableMessaging = async () => {
    setIsAcknowledging(true);
    onEnableMessaging();
    onClose();
    setIsAcknowledging(false);
  };

  const handleNotNow = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute top-0 left-0 w-full h-full bg-white/10 backdrop-blur-sm"
          onClick={onClose}
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", duration: 0.5, bounce: 0.3 }}
          className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white rounded-3xl shadow-2xl border border-white/20 overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-purple-50 opacity-50" />

          <div className="relative p-8 pb-3">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl text-white shadow-lg">
                  <MessageSquare className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Before you continue</h2>
                  <p className="text-sm text-gray-600 mt-1">Messaging charges and acknowledgment</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="mb-4">
              <h3 className="text-base font-semibold text-gray-900 mb-3">Platform access includes:</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-2 bg-blue-500 rounded-lg text-white">
                      <Users className="w-4 h-4" />
                    </div>
                    <span className="font-medium text-blue-900">Registration & Engagement</span>
                  </div>
                  <p className="text-xs text-blue-700">Voter registration tools and engagement features</p>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border border-purple-200"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-2 bg-purple-500 rounded-lg text-white">
                      <Settings className="w-4 h-4" />
                    </div>
                    <span className="font-medium text-purple-900">Dashboards & Reporting</span>
                  </div>
                  <p className="text-xs text-purple-700">Real-time analytics and comprehensive reporting</p>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-2 bg-green-500 rounded-lg text-white">
                      <Shield className="w-4 h-4" />
                    </div>
                    <span className="font-medium text-green-900">Compliance-Safe Exports</span>
                  </div>
                  <p className="text-xs text-green-700">Secure data export and compliance management</p>
                </motion.div>
              </div>
            </div>

            {/* Messaging Delivery Section */}
            <div className="mb-3">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Messaging delivery (SMS/email):</h3>
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-amber-500 rounded-lg text-white">
                    <Mail className="w-4 h-4" />
                  </div>
                  <div className="flex-1">
                    <ul className="space-y-2 text-sm text-amber-800">
                      <li className="flex items-start gap-2">
                        <span className="text-amber-600 mt-0.5">•</span>
                        <span>Uses third-party services (Twilio, SendGrid, etc.)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-600 mt-0.5">•</span>
                        <span>Not included in platform access</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-600 mt-0.5">•</span>
                        <span>Billed per message sent</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Acknowledgment Section */}
            <div className="mb-6">
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-yellow-500 rounded-lg text-white">
                    <DollarSign className="w-4 h-4" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-yellow-900 mb-2">Before you continue</h4>
                    <ul className="space-y-2 text-sm text-yellow-800">
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                        <span>Messaging charges apply only when messages are sent</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                        <span>Charges are usage-based and separate from platform access</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                        <span>Messaging permissions apply at the organization level</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                        <span>Canvassers and team members will only be able to send messages if messaging is enabled for your organization</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pb-4">
              <Button
                label="Not Now"
                variant="secondary"
                size="extra-large"
                icon={<ArrowLeft className="w-4 h-4" />}
                onClick={handleNotNow}
              // className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors font-medium"
              >
              </Button>

              <Button
                size="extra-large"
                variant="primary"
                label={isAcknowledging ? "Enabling..." : "Enable Messaging"}
                onClick={handleEnableMessaging}
                disabled={isAcknowledging}
              // className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-xl transition-all font-medium shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                
              </Button>
            </div>

            {/* Footer Note */}
            <p className="text-center text-xs text-gray-500 mt-4">
              You can change messaging settings at any time in Organization Settings.
            </p>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
